import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Search,
  Edit3,
  Trash2,
  Save,
  X,
  Loader2,
  Users,
  Shield,
  ShieldOff,
  CheckCircle,
  XCircle,
  KeyRound,
} from "lucide-react";
import {
  getAdminUsers,
  updateAdminUser,
  deleteAdminUser,
  resetAdminUserPassword,
} from "../../services/api";
import "./ContentStyles.css";

function UserManagementContent() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  // Edit form state
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async (search) => {
    try {
      setLoading(true);
      const data = await getAdminUsers(search || "");
      if (data.success) {
        setUsers(data.users || []);
      }
    } catch (error) {
      setMessage("❌ " + t("userManagement.loadError"));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadUsers(searchTerm);
  };

  const startEdit = (user) => {
    setEditingUser(user.id);
    setEditForm({
      email: user.email,
      first_name: user.full_name?.split(" ")[0] || "",
      last_name: user.full_name?.split(" ").slice(1).join(" ") || "",
      is_active: user.is_active,
      is_staff: user.is_staff,
      tokens: user.tokens,
      email_verified: user.email_verified,
    });
    setMessage("");
  };

  const cancelEdit = () => {
    setEditingUser(null);
    setEditForm({});
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const data = await updateAdminUser(editingUser, editForm);
      if (data.success) {
        setMessage("✅ " + t("userManagement.userUpdated"));
        setEditingUser(null);
        loadUsers(searchTerm);
      } else {
        setMessage("❌ " + (data.error || t("userManagement.updateError")));
      }
    } catch (error) {
      const errMsg = error.response?.data?.error || t("userManagement.updateError");
      setMessage("❌ " + errMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (userId) => {
    try {
      const data = await deleteAdminUser(userId);
      if (data.success) {
        setMessage("✅ " + t("userManagement.userDeleted"));
        setDeleteConfirm(null);
        loadUsers(searchTerm);
      } else {
        setMessage("❌ " + (data.error || t("userManagement.deleteError")));
      }
    } catch (error) {
      const errMsg = error.response?.data?.error || t("userManagement.deleteError");
      setMessage("❌ " + errMsg);
    }
  };
  const handleResetPassword = async () => {
    if (newPassword.length < 8) {
      setMessage("\u274c " + t("userManagement.passwordMinLength"));
      return;
    }
    try {
      setSaving(true);
      const data = await resetAdminUserPassword(resetPasswordUser, newPassword);
      if (data.success) {
        setMessage("\u2705 " + t("userManagement.passwordReset"));
        setResetPasswordUser(null);
        setNewPassword("");
      } else {
        setMessage("\u274c " + (data.error || t("userManagement.passwordResetError")));
      }
    } catch (error) {
      const errMsg = error.response?.data?.error || t("userManagement.passwordResetError");
      setMessage("\u274c " + errMsg);
    } finally {
      setSaving(false);
    }
  };
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div style={{ padding: "20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
        <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: "#1f2937" }}>
          <Users size={20} style={{ marginRight: "8px", verticalAlign: "middle" }} />
          {t("userManagement.title")}
        </h3>
        <form onSubmit={handleSearch} style={{ display: "flex", gap: "8px" }}>
          <div style={{ position: "relative" }}>
            <Search size={14} style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }} />
            <input
              type="text"
              placeholder={t("userManagement.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: "8px 12px 8px 32px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "13px",
                width: "240px",
              }}
            />
          </div>
          <button
            type="submit"
            style={{
              padding: "8px 16px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            {t("userManagement.search")}
          </button>
        </form>
      </div>

      {message && (
        <div style={{ padding: "10px 16px", marginBottom: "16px", borderRadius: "8px", fontSize: "13px", background: message.startsWith("✅") ? "#ecfdf5" : "#fef2f2", color: message.startsWith("✅") ? "#065f46" : "#991b1b" }}>
          {message}
        </div>
      )}

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
          <Loader2 size={24} className="animate-spin" style={{ animation: "spin 1s linear infinite", color: "#667eea" }} />
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: "600", color: "#6b7280" }}>ID</th>
                <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: "600", color: "#6b7280" }}>{t("userManagement.username")}</th>
                <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: "600", color: "#6b7280" }}>{t("userManagement.email")}</th>
                <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: "600", color: "#6b7280" }}>{t("userManagement.name")}</th>
                <th style={{ padding: "10px 12px", textAlign: "center", fontWeight: "600", color: "#6b7280" }}>{t("userManagement.tokens")}</th>
                <th style={{ padding: "10px 12px", textAlign: "center", fontWeight: "600", color: "#6b7280" }}>{t("userManagement.verified")}</th>
                <th style={{ padding: "10px 12px", textAlign: "center", fontWeight: "600", color: "#6b7280" }}>{t("userManagement.staff")}</th>
                <th style={{ padding: "10px 12px", textAlign: "center", fontWeight: "600", color: "#6b7280" }}>{t("userManagement.active")}</th>
                <th style={{ padding: "10px 12px", textAlign: "left", fontWeight: "600", color: "#6b7280" }}>{t("userManagement.joined")}</th>
                <th style={{ padding: "10px 12px", textAlign: "center", fontWeight: "600", color: "#6b7280" }}>{t("userManagement.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                editingUser === user.id ? (
                  <tr key={user.id} style={{ borderBottom: "1px solid #e5e7eb", background: "#f0f4ff" }}>
                    <td style={{ padding: "10px 12px", color: "#9ca3af" }}>{user.id}</td>
                    <td style={{ padding: "10px 12px", fontWeight: "600", color: "#374151" }}>{user.username}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        style={{ padding: "4px 8px", border: "1px solid #d1d5db", borderRadius: "4px", fontSize: "12px", width: "160px" }}
                      />
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <div style={{ display: "flex", gap: "4px" }}>
                        <input
                          type="text"
                          placeholder={t("userManagement.firstName")}
                          value={editForm.first_name}
                          onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                          style={{ padding: "4px 8px", border: "1px solid #d1d5db", borderRadius: "4px", fontSize: "12px", width: "80px" }}
                        />
                        <input
                          type="text"
                          placeholder={t("userManagement.lastName")}
                          value={editForm.last_name}
                          onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                          style={{ padding: "4px 8px", border: "1px solid #d1d5db", borderRadius: "4px", fontSize: "12px", width: "80px" }}
                        />
                      </div>
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "center" }}>
                      <input
                        type="number"
                        min="0"
                        value={editForm.tokens}
                        onChange={(e) => setEditForm({ ...editForm, tokens: parseInt(e.target.value) || 0 })}
                        style={{ padding: "4px 8px", border: "1px solid #d1d5db", borderRadius: "4px", fontSize: "12px", width: "70px", textAlign: "center" }}
                      />
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={editForm.email_verified}
                        onChange={(e) => setEditForm({ ...editForm, email_verified: e.target.checked })}
                      />
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={editForm.is_staff}
                        onChange={(e) => setEditForm({ ...editForm, is_staff: e.target.checked })}
                      />
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={editForm.is_active}
                        onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                      />
                    </td>
                    <td style={{ padding: "10px 12px", color: "#6b7280" }}>{formatDate(user.date_joined)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "center" }}>
                      <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                        <button
                          onClick={handleSave}
                          disabled={saving}
                          style={{
                            padding: "4px 10px",
                            background: "#10b981",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            fontSize: "11px",
                            fontWeight: "600",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          {saving ? <Loader2 size={12} style={{ animation: "spin 1s linear infinite" }} /> : <Save size={12} />}
                          {t("userManagement.save")}
                        </button>
                        <button
                          onClick={cancelEdit}
                          style={{
                            padding: "4px 10px",
                            background: "#6b7280",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            fontSize: "11px",
                            fontWeight: "600",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <X size={12} />
                          {t("userManagement.cancel")}
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr key={user.id} style={{ borderBottom: "1px solid #e5e7eb" }}>
                    <td style={{ padding: "10px 12px", color: "#9ca3af" }}>{user.id}</td>
                    <td style={{ padding: "10px 12px", fontWeight: "600", color: "#374151" }}>{user.username}</td>
                    <td style={{ padding: "10px 12px", color: "#6b7280" }}>{user.email}</td>
                    <td style={{ padding: "10px 12px", color: "#374151" }}>{user.full_name || "—"}</td>
                    <td style={{ padding: "10px 12px", textAlign: "center", fontWeight: "600", color: "#667eea" }}>{user.tokens}</td>
                    <td style={{ padding: "10px 12px", textAlign: "center" }}>
                      {user.email_verified ? (
                        <CheckCircle size={16} style={{ color: "#10b981" }} />
                      ) : (
                        <XCircle size={16} style={{ color: "#ef4444" }} />
                      )}
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "center" }}>
                      {user.is_staff ? (
                        <Shield size={16} style={{ color: "#667eea" }} />
                      ) : (
                        <ShieldOff size={16} style={{ color: "#d1d5db" }} />
                      )}
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "center" }}>
                      {user.is_active ? (
                        <CheckCircle size={16} style={{ color: "#10b981" }} />
                      ) : (
                        <XCircle size={16} style={{ color: "#ef4444" }} />
                      )}
                    </td>
                    <td style={{ padding: "10px 12px", color: "#6b7280", fontSize: "12px" }}>{formatDate(user.date_joined)}</td>
                    <td style={{ padding: "10px 12px", textAlign: "center" }}>
                      <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                        <button
                          onClick={() => startEdit(user)}
                          style={{
                            padding: "4px 10px",
                            background: "#667eea",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            fontSize: "11px",
                            fontWeight: "600",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <Edit3 size={12} />
                          {t("userManagement.edit")}
                        </button>
                        <button
                          onClick={() => { setResetPasswordUser(user.id); setNewPassword(""); }}
                          style={{
                            padding: "4px 10px",
                            background: "#f59e0b",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            fontSize: "11px",
                            fontWeight: "600",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <KeyRound size={12} />
                          {t("userManagement.resetPwd")}
                        </button>
                        {deleteConfirm === user.id ? (
                          <div style={{ display: "flex", gap: "4px" }}>
                            <button
                              onClick={() => handleDelete(user.id)}
                              style={{
                                padding: "4px 10px",
                                background: "#ef4444",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                fontSize: "11px",
                                fontWeight: "600",
                                cursor: "pointer",
                              }}
                            >
                              {t("userManagement.confirmDelete")}
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              style={{
                                padding: "4px 10px",
                                background: "#6b7280",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                fontSize: "11px",
                                fontWeight: "600",
                                cursor: "pointer",
                              }}
                            >
                              {t("userManagement.cancel")}
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirm(user.id)}
                            style={{
                              padding: "4px 10px",
                              background: "#ef4444",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              fontSize: "11px",
                              fontWeight: "600",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <Trash2 size={12} />
                            {t("userManagement.delete")}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan="10" style={{ padding: "40px", textAlign: "center", color: "#9ca3af" }}>
                    {t("userManagement.noUsers")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: "16px", fontSize: "12px", color: "#9ca3af" }}>
        {t("userManagement.totalUsers")}: {users.length}
      </div>

      {/* Reset Password Modal */}
      {resetPasswordUser && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "white", borderRadius: "12px", padding: "24px", width: "400px", maxWidth: "90vw" }}>
            <h4 style={{ margin: "0 0 16px", fontSize: "16px", fontWeight: "700", color: "#1f2937", display: "flex", alignItems: "center", gap: "8px" }}>
              <KeyRound size={18} />
              {t("userManagement.resetPassword")}
            </h4>
            <p style={{ margin: "0 0 12px", fontSize: "13px", color: "#6b7280" }}>
              {t("userManagement.resetPasswordDesc", { username: users.find(u => u.id === resetPasswordUser)?.username || "" })}
            </p>
            <input
              type="password"
              placeholder={t("userManagement.newPassword")}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "13px", marginBottom: "16px", boxSizing: "border-box" }}
            />
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button
                onClick={() => { setResetPasswordUser(null); setNewPassword(""); }}
                style={{ padding: "8px 16px", background: "#6b7280", color: "white", border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: "600", cursor: "pointer" }}
              >
                {t("userManagement.cancel")}
              </button>
              <button
                onClick={handleResetPassword}
                disabled={saving}
                style={{ padding: "8px 16px", background: "#f59e0b", color: "white", border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: "600", cursor: "pointer" }}
              >
                {saving ? t("userManagement.saving") : t("userManagement.resetPwd")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagementContent;
